import type { AxiosInstance } from "axios";

/**
 * Merchant → agency join requests (Flow 1) — mirrors the API's `join-requests` module.
 */
export type JoinRequestState = "pending" | "approved" | "rejected" | "cancelled" | "expired";

export interface JoinRequest {
  readonly id: string;
  readonly state: JoinRequestState;
  readonly requestedEmail: string;
  readonly agencyName: string;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly decidedAt: string | null;
  readonly decidedBy: string | null;
  readonly store: { readonly id: string; readonly displayName: string; readonly shopDomain: string };
  /**
   * THE BILLING WARNING: a paid plan the store's old account keeps paying for once it leaves — never
   * cancelled automatically. Set for a waiting request (the agency's screen) and, for the old
   * account's own people only, for an approved one (the merchant's banner).
   */
  readonly oldPlan: { readonly planName: string; readonly status: string; readonly renewsAt: string | null } | null;
}

export interface StoreJoinStatus {
  readonly canRequest: boolean;
  readonly latest: JoinRequest | null;
}

export interface AgencyRequests {
  readonly available: boolean;
  readonly requests: readonly JoinRequest[];
}

/** From the install-complete page: the gateway's signed claim to the store, no session. */
export async function requestJoinFromInstall(client: AxiosInstance, joinToken: string, agencyEmail: string): Promise<void> {
  await client.post("/public/join-requests", { joinToken, agencyEmail });
}

/** From the dashboard, for the open store. */
export async function requestJoinForStore(client: AxiosInstance, agencyEmail: string): Promise<void> {
  await client.post("/api/v2/join-requests", { agencyEmail });
}

/** An older API sends no `oldPlan`: that is "no warning", never a crash. */
const withPlan = (request: JoinRequest): JoinRequest => ({ ...request, oldPlan: request.oldPlan ?? null });

/**
 * Anything but the expected shape (an API without this route answering something else) reads as
 * "nothing to say": no request, and no form offered.
 */
export async function fetchStoreJoinStatus(client: AxiosInstance): Promise<StoreJoinStatus> {
  const response = await client.get<{ success: boolean; data: Partial<StoreJoinStatus> | null }>("/api/v2/join-requests/store");
  const data = response.data.data;
  return { canRequest: data?.canRequest === true, latest: data?.latest == null ? null : withPlan(data.latest) };
}

export async function fetchAgencyRequests(client: AxiosInstance): Promise<AgencyRequests> {
  const response = await client.get<{ success: boolean; data: Partial<AgencyRequests> | null }>("/api/v2/join-requests");
  const data = response.data.data;
  return { available: data?.available === true, requests: Array.isArray(data?.requests) ? data.requests.map(withPlan) : [] };
}

export async function approveJoinRequest(client: AxiosInstance, id: string, clientName: string): Promise<void> {
  await client.post(`/api/v2/join-requests/${encodeURIComponent(id)}/approve`, { clientName });
}

export async function rejectJoinRequest(client: AxiosInstance, id: string): Promise<void> {
  await client.post(`/api/v2/join-requests/${encodeURIComponent(id)}/reject`);
}

/** What the merchant is told after asking — the same whether or not the address is an agency. */
export const REQUEST_SENT_MESSAGE =
  "Request sent. If this email belongs to an agency on Ecomally, they will be asked to approve adding your store. You can keep using Ecomally meanwhile.";

/** "29 Oct 2026" in the viewer's zone. */
export function formatRequestDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/** "Your own plan (Growth) is still active and renews on 15 Nov 2026." */
export function planDescription(plan: NonNullable<JoinRequest["oldPlan"]>): string {
  return `${plan.planName}${plan.renewsAt === null ? "" : `, renews ${formatRequestDate(plan.renewsAt)}`}`;
}

function planSentence(store: string, agency: string, plan: NonNullable<JoinRequest["oldPlan"]>): string {
  return `${store} is now managed by ${agency}, but your own Ecomally plan (${planDescription(plan)}) is still active and was not cancelled. Contact Ecomally support to cancel it if you no longer need it.`;
}

/** The merchant's banner line for their latest request, or null when there is nothing to say. */
export function joinBanner(
  latest: JoinRequest | null,
): { readonly tone: "info" | "amber"; readonly text: string; readonly askAgain: boolean } | null {
  if (latest === null) return null;
  switch (latest.state) {
    case "pending":
      return { tone: "info", text: `Waiting for ${latest.requestedEmail} to approve adding this store to their agency.`, askAgain: false };
    case "rejected":
      return { tone: "amber", text: `${latest.requestedEmail} did not approve adding this store to their agency.`, askAgain: true };
    case "expired":
      return { tone: "amber", text: `The request to ${latest.requestedEmail} expired before it was approved.`, askAgain: true };
    case "approved":
      // Joined — nothing to say, unless the old account is still paying for a plan.
      return latest.oldPlan === null
        ? null
        : { tone: "amber", text: `${planSentence(latest.store.displayName, latest.agencyName, latest.oldPlan)}`, askAgain: false };
    case "cancelled":
      return null;
  }
}
